import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { AttachmentService, FlatRenterMappingService, TokenStorageService } from '../../core/services';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import { DetailAssignComponent } from './detail-assign/detail-assign.component';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';
import { AssignSaveEvent, NewEditAssignComponent } from './new-edit-assign/new-edit-assign.component';
import {
  ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE,
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE,
  FeatureTypeEnum
} from '../../core/constants/app-constants';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import { LanguageService } from '../../core/services/language.service';

interface MappingAttachment {
  id: number;
  attachmentFileName?: string;
  attachmentUrl?: string;
}

interface Mapping {
  id: number;
  flat?: { id?: number; flatName?: string; floorName?: string; house?: { houseName?: string; houseNo?: string } };
  renter?: { id?: number; renterName?: string; renterMobile?: string; renterEmail?: string };
  advanceAmount?: number;
  usedAdvanceAmount?: number;
  renterStartDate?: string;
  status: number;
  createdOn?: string;
  updatedOn?: string | null;
  attachments?: MappingAttachment[];
}

interface ApiResponse {
  code: string;
  message: string;
  totalRows: number;
  data: Mapping[];
}

interface MappingRow extends Mapping {
  renterTopLine: string;
  renterBottomLine: string;
  flatLabel: string;
  startDateLabel: string;
  statusLabelText: string;
}

interface UploadFileInsight {
  key: string;
  originalSize: number;
  compressedSize: number | null;
  wasCompressed: boolean;
  isPreparing: boolean;
  exceedsLimit: boolean;
  message: string | null;
  preparedFile: File | null;
}

interface DocumentCompressionResult {
  preparedFile: File;
  compressedPreviewSize: number | null;
  wasCompressed: boolean;
}

@Component({
  selector: 'app-assign-flat',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, EntityListComponent, NewEditAssignComponent, DetailAssignComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './assign-flat.component.html',
  styleUrls: ['./assign-flat.component.css']
})
export class AssignFlatComponent implements OnInit {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  mappingList: MappingRow[] = [];
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();

  selectedMapping: Mapping | null = null;
  preselectedRenter: Record<string, any> | null = null;
  pendingDeleteMapping: Mapping | null = null;
  isDetailsOpen = false;
  isEditOpen = false;
  isDeleting = false;
  isUploadModalOpen = false;
  isUploadingAttachment = false;
  isPreparingUploadFiles = false;
  uploadFeatureId: number | null = null;
  uploadFiles: File[] = [];
  uploadFileInsights: UploadFileInsight[] = [];
  uploadExistingAttachments: MappingAttachment[] = [];
  deletingAttachmentIds: number[] = [];
  uploadMode: 'create' | 'edit' = 'create';
  uploadError: string | null = null;
  pendingDeleteOnlyUpload = false;
  imagePreviewUrl: string | null = null;
  imagePreviewName: string | null = null;
  isImagePreviewOpen = false;
  readonly assignmentFeatureType = FeatureTypeEnum.FLAT_RENTER_AGREEMENT;
  readonly assignmentMaxFileCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[FeatureTypeEnum.FLAT_RENTER_AGREEMENT];
  readonly assignmentAllowedExtensionsText = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[FeatureTypeEnum.FLAT_RENTER_AGREEMENT].join(', ');

  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.renter': {
      topLineKey: 'renterTopLine',
      bottomLineKyes: ['renterBottomLine']
    },
    'table.flat': 'flatLabel',
    'table.startDate': 'startDateLabel'
  };
  readonly assignMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'FLAT_RENTER_MAPPING_DELETE' }
  ];

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  constructor(
    private readonly mappings: FlatRenterMappingService,
    private readonly tokens: TokenStorageService,
    private readonly attachments: AttachmentService,
    private readonly language: LanguageService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (this.isAuthenticated) {
      this.status = 'Loading assign flat list...';
      this.fetchMappings(true);
    } else {
      this.error = 'Please log in first.';
    }
  }

  get hasMoreMappings(): boolean {
    return hasMoreByTotal(this.mappingList, this.paginationState);
  }

  fetchMappings(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreMappings))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.mappingList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.mappings.list({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      filters
    })
      .pipe(finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }))
      .subscribe({
        next: (res: ApiResponse) => {
          if (res && res.code === '000') {
            const nextRows = (res.data || []).map((item) => ({
              ...item,
              renterTopLine: item.renter?.renterName || 'N/A',
              renterBottomLine: item.renter?.renterMobile || 'N/A',
              flatLabel: this.buildFlatLabel(item),
              startDateLabel: this.formatDate(item.renterStartDate),
              statusLabelText: item.status === 1 ? 'Active' : 'Inactive'
            }));
            updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
            this.mappingList = reset ? nextRows : appendUniqueById(this.mappingList, nextRows);

            const selectedMappingId = Number(this.selectedMapping?.id);
            if (Number.isFinite(selectedMappingId)) {
              const latestSelectedMapping = this.mappingList.find((item) => Number(item.id) === selectedMappingId);
              if (latestSelectedMapping) {
                this.selectedMapping = latestSelectedMapping;
              }
            }

            this.status = `Total mapped flats: ${this.paginationState.totalRows || this.mappingList.length}`;
          } else {
            this.error = res?.message || 'Unknown error occurred.';
          }
        },
        error: (err: any) => {
          this.error = err?.message || 'Error fetching assign flat list';
        }
      });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchMappings(true);
  }

  onLoadMoreFromList(): void {
    this.fetchMappings(false);
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedMapping = item as Mapping;
    this.isDetailsOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedMapping = item as Mapping;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteMapping = item as Mapping;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteMapping = null;
  }

  confirmDeleteMapping(): void {
    const mapping = this.pendingDeleteMapping;
    if (!mapping || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;

    this.mappings.delete({ id: mapping.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeleteMapping = null;
          this.fetchMappings(true);
        },
        error: (err: any) => {
          this.pendingDeleteMapping = null;
          this.error = err?.message || 'Failed to delete mapping.';
        }
      });
  }

  openCreateModal(preselectedRenter?: Record<string, any> | null): void {
    this.selectedMapping = null;
    this.preselectedRenter = preselectedRenter ? { ...preselectedRenter } : null;
    this.isEditOpen = true;
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedMapping = null;
    this.preselectedRenter = null;
  }

  onEditSave(event?: AssignSaveEvent): void {
    const previousSelectedMapping = this.selectedMapping;
    this.isEditOpen = false;
    this.selectedMapping = null;
    this.preselectedRenter = null;
    this.fetchMappings(true);

    if (event?.mode === 'create' && Number.isFinite(Number(event?.refId))) {
      this.uploadFeatureId = Number(event.refId);
      this.uploadMode = event.mode;
      this.uploadFiles = [];
      this.deletingAttachmentIds = [];
      this.uploadExistingAttachments = Array.isArray(previousSelectedMapping?.attachments)
        ? [...(previousSelectedMapping?.attachments as MappingAttachment[])]
        : [];
      this.uploadError = null;
      this.isUploadModalOpen = true;
    }
  }

  openEditDocumentsModal(): void {
    if (!this.selectedMapping || !Number.isFinite(Number(this.selectedMapping.id))) {
      return;
    }

    this.uploadFeatureId = Number(this.selectedMapping.id);
    this.uploadMode = 'edit';
    this.uploadFiles = [];
    this.deletingAttachmentIds = [];
    this.uploadExistingAttachments = Array.isArray(this.selectedMapping.attachments)
      ? [...(this.selectedMapping.attachments as MappingAttachment[])]
      : [];
    this.uploadError = null;
    this.isUploadModalOpen = true;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    const hours = `${hours12}`.padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`;
  }

  private buildFlatLabel(item: Mapping): string {
    const flatName = item.flat?.flatName || 'N/A';
    const floorName = item.flat?.floorName ? `Floor #${item.flat.floorName}` : '';
    const houseName = item.flat?.house?.houseName || '';
    const suffix = [floorName, houseName].filter((v) => !!v).join(', ');
    return suffix ? `${flatName} (${suffix})` : flatName;
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatAmount(value: number | null | undefined): string {
    return `৳${Number(value ?? 0).toLocaleString('en-US')}`;
  }

  remainingAdvanceAmount(item: Mapping | null | undefined): number {
    const advance = Number(item?.advanceAmount ?? 0);
    const used = Number(item?.usedAdvanceAmount ?? 0);
    return Math.max(0, advance - used);
  }

  onUploadFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const incoming = Array.from(input?.files ?? []);
    if (!incoming.length) {
      if (input) {
        input.value = '';
      }
      return;
    }

    this.uploadError = null;
    const oversizeNonImageFiles: File[] = [];
    const validIncomingFiles = incoming.filter((file) => {
      const isOversizeNonImage = !this.isCompressibleImageFile(file) && Number(file?.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES;
      if (isOversizeNonImage) {
        oversizeNonImageFiles.push(file);
        return false;
      }
      return true;
    });

    const current = [...this.uploadFiles];
    const merged = [...current];
    const existingNames = new Set(
      this.uploadExistingAttachments
        .map((item) => `${item?.attachmentFileName ?? ''}`.trim().toLowerCase())
        .filter((name) => !!name)
    );
    const duplicateNames: string[] = [];

    validIncomingFiles.forEach((file) => {
      const normalizedName = `${file?.name ?? ''}`.trim().toLowerCase();
      const existsInSelected = merged.some(
        (item) => `${item?.name ?? ''}`.trim().toLowerCase() === normalizedName
      );
      const exists = existsInSelected || existingNames.has(normalizedName);
      if (!exists) {
        merged.push(file);
      } else if (normalizedName) {
        duplicateNames.push(file.name);
      }
    });
    this.uploadFiles = merged;

    if (oversizeNonImageFiles.length > 0) {
      const firstFile = oversizeNonImageFiles[0];
      this.uploadError = this.buildOversizeFileMessage(firstFile);
    } else if (duplicateNames.length > 0) {
      this.uploadError = `Duplicate file name not allowed: ${duplicateNames.join(', ')}`;
    } else {
      this.uploadError = null;
    }

    validIncomingFiles
      .filter((file) => this.isCompressibleImageFile(file))
      .forEach((file) => this.ensureUploadFileInsight(file));

    if (input) {
      input.value = '';
    }
  }

  uploadAssignmentDocuments(): void {
    if (!this.uploadFeatureId) {
      this.uploadError = 'Invalid assignment id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (willDeleteOnly) {
      this.pendingDeleteOnlyUpload = true;
      return;
    }
    void this.performAssignmentUpload();
  }

  confirmDeleteOnlyUpload(): void {
    if (!this.pendingDeleteOnlyUpload) {
      return;
    }
    this.pendingDeleteOnlyUpload = false;
    void this.performAssignmentUpload();
  }

  closeDeleteOnlyUploadModal(): void {
    this.pendingDeleteOnlyUpload = false;
  }

  private async performAssignmentUpload(): Promise<void> {
    const featureId = this.uploadFeatureId;
    if (!featureId) {
      this.uploadError = 'Invalid assignment id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (this.isPreparingUploadFiles) {
      this.uploadError = 'Please wait while selected files are being prepared.';
      return;
    }

    if (!this.uploadFiles.length && !willDeleteOnly) {
      this.uploadError = 'Please select at least one file.';
      return;
    }

    const featureType = this.assignmentFeatureType;
    const maxCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[featureType];
    const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[featureType];

    const finalCount = this.uploadExistingAttachments.length + this.uploadFiles.length;
    if (finalCount > maxCount) {
      this.uploadError = `Maximum ${maxCount} file(s) allowed for additional documents.`;
      return;
    }

    const invalid = this.uploadFiles.find((file) => {
      const name = `${file.name ?? ''}`.toLowerCase();
      return !allowedExtensions.some((ext) => name.endsWith(ext));
    });

    if (invalid) {
      this.uploadError = `Only ${this.assignmentAllowedExtensionsText} files are allowed.`;
      return;
    }

    this.isPreparingUploadFiles = true;

    let preparedFiles: File[];
    try {
      preparedFiles = await Promise.all(this.uploadFiles.map((file) => this.prepareDocumentUploadFile(file)));
    } catch (error: any) {
      this.uploadError = error?.message || 'Failed to prepare selected files.';
      this.isPreparingUploadFiles = false;
      return;
    }

    if (!this.isUploadModalOpen) {
      this.isPreparingUploadFiles = false;
      return;
    }

    const preparedNames = new Set<string>();
    const existingNames = new Set(
      this.uploadExistingAttachments
        .map((item) => `${item?.attachmentFileName ?? ''}`.trim().toLowerCase())
        .filter((name) => !!name)
    );
    const duplicatePreparedNames: string[] = [];
    preparedFiles.forEach((file) => {
      const normalizedName = `${file?.name ?? ''}`.trim().toLowerCase();
      if (!normalizedName) {
        return;
      }
      if (preparedNames.has(normalizedName) || existingNames.has(normalizedName)) {
        duplicatePreparedNames.push(file.name);
        return;
      }
      preparedNames.add(normalizedName);
    });

    if (duplicatePreparedNames.length > 0) {
      this.uploadError = `Duplicate file name not allowed: ${duplicatePreparedNames.join(', ')}`;
      this.isPreparingUploadFiles = false;
      return;
    }

    const oversize = preparedFiles.find((file) => Number(file?.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES);
    if (oversize) {
      this.uploadError = this.buildOversizeFileMessage(oversize);
      this.isPreparingUploadFiles = false;
      return;
    }

    this.isUploadingAttachment = true;
    this.uploadError = null;
    this.isPreparingUploadFiles = false;

    this.attachments.manageAttachment({
      featureType,
      featureId,
      actionType: this.uploadMode === 'edit' ? 2 : 1,
      files: preparedFiles,
      deletingIds: this.deletingAttachmentIds,
      service: 'FLAT_RENTER_AGREEMENT_IMAGE_CREATE'
    })
      .pipe(finalize(() => (this.isUploadingAttachment = false)))
      .subscribe({
        next: () => {
          if (this.uploadMode === 'edit') {
      this.snackBar.open(this.language.translate('assign.documentsUpdatedSuccess', 'Additional documents updated successfully.'), this.language.translate('common.close', 'Close'), {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top'
            });
          }
          this.closeUploadModal();
          this.fetchMappings(true);
        },
        error: (err: any) => {
          this.uploadError = err?.message || 'Failed to upload attachment(s).';
        }
      });
  }

  closeUploadModal(): void {
    this.isUploadModalOpen = false;
    this.isPreparingUploadFiles = false;
    this.uploadFeatureId = null;
    this.uploadFiles = [];
    this.uploadFileInsights = [];
    this.uploadExistingAttachments = [];
    this.deletingAttachmentIds = [];
    this.uploadMode = 'create';
    this.uploadError = null;
    this.pendingDeleteOnlyUpload = false;
  }

  skipUploadModal(): void {
    this.closeUploadModal();
  }

  removeUploadFile(index: number): void {
    if (index < 0 || index >= this.uploadFiles.length) {
      return;
    }
    const removedFile = this.uploadFiles[index];
    this.uploadFiles = this.uploadFiles.filter((_, i) => i !== index);
    this.removeUploadFileInsight(removedFile);
  }

  uploadFileInsight(file: File): UploadFileInsight | null {
    const key = this.buildUploadFileKey(file);
    return this.uploadFileInsights.find((item) => item.key === key) || null;
  }

  formatFileSize(bytes: number | null | undefined): string {
    const safeBytes = Number(bytes ?? 0);
    if (!safeBytes) {
      return '0 KB';
    }
    if (safeBytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(safeBytes / 1024))} KB`;
    }
    return `${(safeBytes / (1024 * 1024)).toFixed(safeBytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }

  removeExistingAttachment(index: number): void {
    const target = this.uploadExistingAttachments[index];
    if (!target) {
      return;
    }

    const id = Number(target.id);
    if (Number.isFinite(id) && !this.deletingAttachmentIds.includes(id)) {
      this.deletingAttachmentIds.push(id);
    }
    this.uploadExistingAttachments = this.uploadExistingAttachments.filter((_, i) => i !== index);
  }

  openAttachmentPreview(attachment: MappingAttachment): void {
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      const fallbackUrl = `${attachment?.attachmentUrl ?? ''}`.trim();
      if (fallbackUrl) {
        this.openPreviewByType(fallbackUrl, `${attachment?.attachmentFileName ?? ''}`);
      }
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => {
        this.openPreviewByType(url, `${attachment?.attachmentFileName ?? ''}`);
      },
      error: (err: any) => {
        this.uploadError = err?.message || 'Failed to open attachment preview.';
      }
    });
  }

  private openPreviewByType(url: string, fileNameRaw: string): void {
    const fileName = `${fileNameRaw ?? ''}`.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
      return;
    }

    this.imagePreviewUrl = url;
    this.imagePreviewName = fileNameRaw || 'Attachment';
    this.isImagePreviewOpen = true;
  }

  closeImagePreview(): void {
    this.isImagePreviewOpen = false;
    this.imagePreviewUrl = null;
    this.imagePreviewName = null;
  }

  async downloadPreviewImage(): Promise<void> {
    const url = `${this.imagePreviewUrl ?? ''}`.trim();
    if (!url) {
      return;
    }

    const fileName = this.imagePreviewName || 'image';
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
    }
  }

  private async prepareDocumentUploadFile(file: File): Promise<File> {
    if (!this.isCompressibleImageFile(file)) {
      return file;
    }

    const cachedPreparedFile = this.uploadFileInsight(file)?.preparedFile;
    const preparedFile = cachedPreparedFile ?? (await this.compressDocumentImage(file)).preparedFile;
    if (Number(preparedFile?.size ?? 0) > this.targetImageUploadSizeBytes()) {
      throw new Error(`Compressed size should not pass ${this.targetImageUploadSizeLabel()}.`);
    }

    return preparedFile;
  }

  private isCompressibleImageFile(file: File): boolean {
    const type = `${file?.type ?? ''}`.toLowerCase();
    const name = `${file?.name ?? ''}`.toLowerCase();
    return type.startsWith('image/') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
  }

  private async compressDocumentImage(file: File): Promise<DocumentCompressionResult> {
    const image = await this.loadImageElement(file);
    const naturalWidth = Number(image.naturalWidth || image.width || 0);
    const naturalHeight = Number(image.naturalHeight || image.height || 0);
    if (!naturalWidth || !naturalHeight) {
      return {
        preparedFile: file,
        compressedPreviewSize: null,
        wasCompressed: false
      };
    }

    const targetBytes = this.targetImageUploadSizeBytes();
    const maxDimension = 1440;
    const initialScale = Math.min(1, maxDimension / Math.max(naturalWidth, naturalHeight));
    let scale = initialScale;
    let bestBlob: Blob | null = null;

    while (scale >= 0.18) {
      const outputWidth = Math.max(1, Math.round(naturalWidth * scale));
      const outputHeight = Math.max(1, Math.round(naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        return {
          preparedFile: file,
          compressedPreviewSize: null,
          wasCompressed: false
        };
      }

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, outputWidth, outputHeight);
      context.drawImage(image, 0, 0, outputWidth, outputHeight);

      for (const quality of [0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.34, 0.28]) {
        const blob = await this.canvasToJpegBlob(canvas, quality);
        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
        }
        if (blob.size <= targetBytes) {
          return {
            preparedFile: this.buildCompressedDocumentFile(file, blob),
            compressedPreviewSize: blob.size,
            wasCompressed: true
          };
        }
      }

      scale *= 0.82;
    }

    if (!bestBlob || bestBlob.size >= file.size) {
      return {
        preparedFile: file,
        compressedPreviewSize: bestBlob?.size ?? file.size,
        wasCompressed: false
      };
    }

    return {
      preparedFile: this.buildCompressedDocumentFile(file, bestBlob),
      compressedPreviewSize: bestBlob.size,
      wasCompressed: true
    };
  }

  private loadImageElement(file: File): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to read image.'));
      };
      image.src = objectUrl;
    });
  }

  private canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to compress image.'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', quality);
    });
  }

  private buildCompressedDocumentFile(originalFile: File, blob: Blob): File {
    return new File([blob], this.buildCompressedDocumentFileName(originalFile.name), {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  }

  private buildCompressedDocumentFileName(originalName: string | undefined): string {
    const name = `${originalName ?? ''}`.trim();
    const extensionMatch = /\.([^.]+)$/.exec(name.toLowerCase());
    const extension = `${extensionMatch?.[1] ?? ''}`.trim();
    const rawBaseName = name.replace(/\.[^.]+$/, '').trim() || 'document-image';
    const sanitizedBaseName = rawBaseName
      .normalize('NFKD')
      .replace(/[^\x20-\x7E]/g, '')
      .replace(/[^a-zA-Z0-9._ -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[.\-_\s]+|[.\-_\s]+$/g, '')
      .slice(0, 70);
    const suffix = extension && extension !== 'jpg' && extension !== 'jpeg'
      ? `-${extension}`
      : '';
    const finalBaseName = sanitizedBaseName || 'document-image';
    return `${finalBaseName}${suffix}-compressed.jpg`;
  }

  private describeDocumentFileType(file: File): string {
    const name = `${file?.name ?? ''}`.trim().toLowerCase();
    const extensionMatch = /\.([^.]+)$/.exec(name);
    const extension = `${extensionMatch?.[1] ?? ''}`.trim();
    if (extension) {
      return extension.toUpperCase();
    }

    const type = `${file?.type ?? ''}`.trim().toLowerCase();
    if (type.includes('/')) {
      return type.split('/').pop()?.toUpperCase() || 'FILE';
    }

    return 'FILE';
  }

  private maxUploadSizeLabel(): string {
    const sizeInMb = ATTACHMENT_MAX_FILE_SIZE_BYTES / (1024 * 1024);
    return Number.isInteger(sizeInMb) ? `${sizeInMb} MB` : `${sizeInMb.toFixed(1)} MB`;
  }

  private targetImageUploadSizeBytes(): number {
    return 1 * 1024 * 1024;
  }

  private targetImageUploadSizeLabel(): string {
    const sizeInMb = this.targetImageUploadSizeBytes() / (1024 * 1024);
    return Number.isInteger(sizeInMb) ? `${sizeInMb} MB` : `${sizeInMb.toFixed(1)} MB`;
  }

  private buildOversizeFileMessage(file: File): string {
    return `${this.describeDocumentFileType(file)} file not allowed more than ${this.maxUploadSizeLabel()}.`;
  }

  private buildUploadFileKey(file: File): string {
    return [`${file?.name ?? ''}`.trim().toLowerCase(), Number(file?.size ?? 0), Number(file?.lastModified ?? 0)].join('|');
  }

  private ensureUploadFileInsight(file: File): void {
    const key = this.buildUploadFileKey(file);
    if (this.uploadFileInsights.some((item) => item.key === key)) {
      return;
    }

    this.uploadFileInsights = [
      ...this.uploadFileInsights,
      {
        key,
        originalSize: Number(file?.size ?? 0),
        compressedSize: null,
        wasCompressed: false,
        isPreparing: true,
        exceedsLimit: false,
        message: null,
        preparedFile: null
      }
    ];

    void this.refreshUploadFileInsight(file);
  }

  private removeUploadFileInsight(file: File | undefined): void {
    if (!file) {
      return;
    }
    const key = this.buildUploadFileKey(file);
    this.uploadFileInsights = this.uploadFileInsights.filter((item) => item.key !== key);
  }

  private async refreshUploadFileInsight(file: File): Promise<void> {
    const key = this.buildUploadFileKey(file);

    try {
      const compressionResult = await this.compressDocumentImage(file);
      if (!this.hasSelectedUploadFile(key)) {
        return;
      }

      const preparedFile = compressionResult.preparedFile;
      const wasCompressed = compressionResult.wasCompressed;
      const compressedSize = Number(compressionResult.compressedPreviewSize ?? 0);
      const exceedsLimit = compressedSize > this.targetImageUploadSizeBytes();
      this.patchUploadFileInsight(key, {
        compressedSize: compressionResult.compressedPreviewSize,
        wasCompressed,
        isPreparing: false,
        exceedsLimit,
        message: exceedsLimit
          ? (wasCompressed
              ? `Compressed size should not pass ${this.targetImageUploadSizeLabel()}.`
              : `Best compressed size is still above ${this.targetImageUploadSizeLabel()}.`)
          : (wasCompressed ? null : 'Compression preview is close to the original size.'),
        preparedFile
      });
    } catch {
      if (!this.hasSelectedUploadFile(key)) {
        return;
      }

      this.patchUploadFileInsight(key, {
        wasCompressed: false,
        isPreparing: false,
        exceedsLimit: false,
        message: 'Unable to estimate compressed size.',
        preparedFile: null
      });
    }
  }

  private hasSelectedUploadFile(key: string): boolean {
    return this.uploadFiles.some((file) => this.buildUploadFileKey(file) === key);
  }

  private patchUploadFileInsight(key: string, patch: Partial<UploadFileInsight>): void {
    this.uploadFileInsights = this.uploadFileInsights.map((item) => (
      item.key === key
        ? { ...item, ...patch }
        : item
    ));
  }

}
